import {
  PickingTool,
  type PickEventDetail,
  type Web3DViewer,
} from '@technology/web3d';
import { SnapType } from '@trimble/web3d-providers';
import {
  defaultPaneEligibility,
  getEligibleViewers,
  type PaneEligibilityOptions,
} from './pane-eligibility';
import {
  CapabilityCoordinatorBase,
  type CapabilityRegistry,
} from '@trimble/web3d';

export interface MarkerPoint {
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

export type IssueMarkerPickCoordinatorOptions = PaneEligibilityOptions;

/**
 * Cross-pane coordinator that handles marker placement via Web3D picking.
 * Only one pick can be active at a time; starting a new pick or calling
 * `cancelPick()` cancels any pending one.
 */
export class IssueMarkerPickCoordinator extends CapabilityCoordinatorBase<CapabilityRegistry> {
  private readonly isPaneEligible: NonNullable<
    IssueMarkerPickCoordinatorOptions['isPaneEligible']
  >;

  private cancelPendingPick: (() => void) | null = null;

  constructor(
    registry: CapabilityRegistry,
    options: IssueMarkerPickCoordinatorOptions = {}
  ) {
    super(registry);
    this.isPaneEligible = options.isPaneEligible ?? defaultPaneEligibility;
  }

  public pickMarkerPosition(): Promise<MarkerPoint | null> {
    this.cancelPendingPick?.();

    const viewers = this.getEligibleViewers();
    if (viewers.length === 0) {
      return Promise.resolve(null);
    }

    const savedStates = viewers.map((viewer) => {
      const pickingTool = viewer.tools.get(PickingTool);
      const prev = {
        enabled: pickingTool?.enabled ?? false,
        activeTool: viewer.activeTool ?? '',
        snapTypes: [...viewer.settings.snapTypes],
      };
      if (pickingTool) {
        pickingTool.enabled = true;
        viewer.activeTool = pickingTool.name;
      }
      viewer.settings.snapTypes = [
        SnapType.FACE,
        SnapType.LINE,
        SnapType.POINT,
      ] as number[];
      return { viewer, pickingTool, prev };
    });

    const restoreAll = (): void => {
      for (const { viewer, pickingTool, prev } of savedStates) {
        viewer.settings.snapTypes = prev.snapTypes;
        viewer.activeTool = prev.activeTool;
        if (pickingTool) {
          pickingTool.enabled = prev.enabled;
        }
      }
    };

    const removeAllListeners = (handler: EventListener): void => {
      for (const { viewer } of savedStates) {
        viewer.removeEventListener('pick', handler);
      }
    };

    return new Promise<MarkerPoint | null>((resolve) => {
      const handlePick = (event: Event): void => {
        const detail = (event as CustomEvent<PickEventDetail>).detail;
        if (detail.hover) return;

        const worldPos = detail.worldPosition;
        if (!worldPos) {
          return;
        }

        removeAllListeners(handlePick as EventListener);
        this.cancelPendingPick = null;
        restoreAll();

        resolve({ x: worldPos.x, y: worldPos.y, z: worldPos.z });
      };

      for (const { viewer } of savedStates) {
        viewer.addEventListener('pick', handlePick as EventListener);
      }

      this.cancelPendingPick = (): void => {
        removeAllListeners(handlePick as EventListener);
        this.cancelPendingPick = null;
        restoreAll();
        resolve(null);
      };
    });
  }

  public cancelPick(): void {
    this.cancelPendingPick?.();
  }

  public async cleanup(): Promise<void> {
    this.cancelPendingPick?.();
  }

  private getEligibleViewers(): Web3DViewer[] {
    return getEligibleViewers(this.registry, this.isPaneEligible);
  }
}
